import React, { useState, useEffect, useCallback, useRef } from "react";
import { PhoneCall, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const PROMPT_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Gets pending call from localStorage
 */
const getPendingCall = () => {
  try {
    const stored = localStorage.getItem("pendingCall");
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

/**
 * Clears pending call from localStorage
 */
const clearPendingCall = () => {
  localStorage.removeItem("pendingCall");
};

/**
 * Checks if pending call is still valid (within timeout)
 */
const isPendingCallValid = (pendingCall) => {
  if (!pendingCall || !pendingCall.started_at) return false;
  const startedAt = new Date(pendingCall.started_at).getTime();
  const now = Date.now();
  return now - startedAt < PROMPT_TIMEOUT_MS;
};

/**
 * PostCallPrompt - Shows a prompt after user returns from making a call
 * 
 * Props:
 * - onLogCall: Callback when user wants to log the call, receives { contactId, contactName }
 * - lastCallLoggedAt: Timestamp of the last logged call (to avoid prompting if already logged)
 */
const PostCallPrompt = ({ onLogCall, lastCallLoggedAt }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingCall, setPendingCall] = useState(null);
  const shownForCallIdRef = useRef(null); // Track which call we've shown prompt for

  const checkAndShowPrompt = useCallback(() => {
    const pending = getPendingCall();
    
    // No pending call or expired
    if (!pending || !isPendingCallValid(pending)) {
      clearPendingCall();
      return;
    }

    // Don't show if already shown for this exact call (by started_at timestamp)
    if (shownForCallIdRef.current === pending.started_at) {
      return;
    }

    // Check if user already logged a call after this call started
    if (lastCallLoggedAt) {
      const callStarted = new Date(pending.started_at).getTime();
      const lastLogged = new Date(lastCallLoggedAt).getTime();
      if (lastLogged > callStarted) {
        // User already logged a call after starting this one
        clearPendingCall();
        return;
      }
    }

    // Show the prompt and mark as shown for this call
    shownForCallIdRef.current = pending.started_at;
    setPendingCall(pending);
    setShowPrompt(true);
  }, [lastCallLoggedAt]);

  useEffect(() => {
    // Listen for visibility changes (user returning to app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Small delay to let the page settle
        setTimeout(checkAndShowPrompt, 500);
      }
    };

    // Listen for focus (backup for visibility)
    const handleFocus = () => {
      setTimeout(checkAndShowPrompt, 500);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkAndShowPrompt]);

  const handleLogCall = () => {
    if (pendingCall && onLogCall) {
      onLogCall({
        contactId: pendingCall.contact_id,
        contactName: pendingCall.contact_name,
      });
    }
    clearPendingCall();
    setShowPrompt(false);
    setPendingCall(null);
  };

  const handleDismiss = () => {
    clearPendingCall();
    setShowPrompt(false);
    setPendingCall(null);
  };

  return (
    <AnimatePresence>
      {showPrompt && pendingCall && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.95 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50"
          data-testid="post-call-prompt"
        >
          <div className="bg-white rounded-2xl shadow-xl border border-[#F4F1EA] p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#81B29A]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <PhoneCall className="w-5 h-5 text-[#81B29A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
                  Log your call?
                </p>
                <p className="text-sm text-[#6D6F7C] mt-0.5">
                  with <span className="font-medium text-[#3D405B]">{pendingCall.contact_name}</span>
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="text-[#6D6F7C] hover:text-[#3D405B] p-1 rounded-full hover:bg-[#F4F1EA] transition-colors"
                aria-label="Dismiss"
                data-testid="post-call-dismiss-x"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex gap-2 mt-4">
              <Button
                onClick={handleLogCall}
                className="flex-1 bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full"
                data-testid="post-call-log-btn"
              >
                <PhoneCall className="w-4 h-4 mr-2" />
                Log Call
              </Button>
              <Button
                onClick={handleDismiss}
                variant="outline"
                className="flex-1 border-[#F4F1EA] text-[#6D6F7C] rounded-full hover:bg-[#F4F1EA]"
                data-testid="post-call-dismiss-btn"
              >
                Not now
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PostCallPrompt;
